import type { ApprovalDecision, BookingStatus, TripType } from '@fleet/shared';
import { apiFetch } from './client';

export interface CalendarVehicle {
  id: string;
  licensePlate: string;
  displayName: string;
  colorHex: string;
  vehicleGroupId: string;
  vehicleGroupName: string;
  canBook: boolean;
}

export interface CalendarEntry {
  id: string;
  bookingNumber: string;
  vehicleId: string;
  startsAt: string;
  endsAt: string;
  status: BookingStatus;
  /** Für alle sichtbar — ohne den Namen ließe sich eine Belegung nicht klären. */
  driverName: string;
  /** Dienstlich oder privat — für alle sichtbar. */
  tripType: TripType;
  /**
   * Kurztext für den Kalender, für alle sichtbar. Von der buchenden Person
   * dafür bestimmt; leer bei Privatfahrten und bei Altbuchungen.
   */
  publicPurpose?: string;
  /**
   * Der volle Anlass — nur bei der eigenen Buchung, für Freigabeberechtigte
   * dieser Fahrzeuggruppe sowie für Fuhrparkleitung und Administration.
   */
  purpose?: string;
  isOwn: boolean;
  /** Darf der Aufrufer diese Belegung stornieren? Vom Server berechnet. */
  canManage: boolean;
  /**
   * Darf der Aufrufer den Zeitraum auch verlängern?
   *
   * Nur Manager dieser Fahrzeuggruppe. Für alle übrigen bleibt es nach der
   * Freigabe beim Verkürzen.
   */
  canExtend: boolean;
}

export interface AvailabilityResponse {
  from: string;
  to: string;
  vehicles: CalendarVehicle[];
  entries: CalendarEntry[];
}

export interface MyBooking {
  id: string;
  bookingNumber: string;
  startsAt: string;
  endsAt: string;
  status: BookingStatus;
  tripType: TripType;
  purpose?: string;
  requiresApproval: boolean;
  /** Nur bei abgelehnten Buchungen gesetzt. */
  rejectionReason?: string;
  /** Darf der Zeitraum auch verlängert werden? Nur für Manager dieser Gruppe. */
  canExtend: boolean;
  vehicle: { id: string; licensePlate: string; displayName: string; colorHex: string };
}

/** Eine Buchung in der Verwaltungsansicht. */
export interface ManagedBooking {
  id: string;
  bookingNumber: string;
  startsAt: string;
  endsAt: string;
  status: BookingStatus;
  tripType: TripType;
  purpose?: string;
  rejectionReason?: string;
  driver: { id: string; displayName: string };
  vehicle: { id: string; licensePlate: string; displayName: string; colorHex: string };
}

/** Eine offene Buchung in der Freigabe-Inbox. */
export interface PendingBooking {
  id: string;
  bookingNumber: string;
  startsAt: string;
  endsAt: string;
  tripType: TripType;
  purpose?: string;
  destinationAddress?: string;
  passengers?: string;
  notes?: string;
  requestedAt: string;
  /** Eigene Buchung — die Oberfläche weist darauf hin. */
  isOwn: boolean;
  driver: { id: string; displayName: string };
  vehicle: {
    id: string;
    licensePlate: string;
    displayName: string;
    colorHex: string;
    vehicleGroupName: string;
  };
}

export interface CreateBookingBody {
  vehicleId: string;
  /** Weggelassen = für sich selbst. Nur Manager dürfen für andere buchen. */
  driverId?: string;
  startsAt: string;
  endsAt: string;
  intendedTripType: TripType;
  purpose?: string;
  /**
   * Kurztext für den Kalender, für alle sichtbar. Bei Privatfahrten weist der
   * Server ihn zurück — dort wird kein Zweck erfasst, auch kein freiwilliger.
   */
  publicPurpose?: string;
  destinationAddress?: string;
  passengers?: string;
  notes?: string;
  /**
   * Bestätigte Fassung der Datenschutzhinweise.
   *
   * Für alle Pflicht, die die betreffende Fahrzeuggruppe nicht selbst freigeben
   * dürfen. Weicht sie von der geltenden ab, antwortet der Server mit
   * PRIVACY_NOTICE_NOT_ACKNOWLEDGED.
   */
  privacyNoticeVersion?: string;
}

export const bookingsApi = {
  availability: (from: Date, to: Date, vehicleGroupId?: string) => {
    const params = new URLSearchParams({ from: from.toISOString(), to: to.toISOString() });
    if (vehicleGroupId) params.set('vehicleGroupId', vehicleGroupId);
    return apiFetch<AvailabilityResponse>(`/api/v1/bookings/availability?${params}`);
  },

  mine: (includePast = false) =>
    apiFetch<MyBooking[]>(`/api/v1/bookings/mine?includePast=${includePast}`),

  create: (body: CreateBookingBody) =>
    apiFetch<{
      id: string;
      bookingNumber: string;
      status: BookingStatus;
      requiresApproval: boolean;
    }>('/api/v1/bookings', { method: 'POST', body: JSON.stringify(body) }),

  cancel: (id: string, reason?: string) =>
    apiFetch<{ id: string; status: BookingStatus }>(`/api/v1/bookings/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  /**
   * Ändert die Zeiten einer Buchung.
   *
   * Vor der Freigabe in beide Richtungen, danach nur verkürzend — ein
   * Verlängern endet mit `BOOKING_CANNOT_EXTEND`.
   */
  reschedule: (id: string, startsAt: Date, endsAt: Date) =>
    apiFetch<{ id: string; startsAt: string; endsAt: string }>(
      `/api/v1/bookings/${id}/reschedule`,
      {
        method: 'POST',
        body: JSON.stringify({ startsAt: startsAt.toISOString(), endsAt: endsAt.toISOString() }),
      },
    ),

  /** Offene Buchungen, über die der Aufrufer entscheiden darf. */
  pending: () => apiFetch<PendingBooking[]>('/api/v1/bookings/pending'),

  /** Alle Buchungen — Verwaltungsansicht, ohne die Zurückhaltung des Kalenders. */
  all: (filter: { status?: BookingStatus; vehicleId?: string; from?: Date; to?: Date } = {}) => {
    const params = new URLSearchParams();
    if (filter.status) params.set('status', filter.status);
    if (filter.vehicleId) params.set('vehicleId', filter.vehicleId);
    if (filter.from) params.set('from', filter.from.toISOString());
    if (filter.to) params.set('to', filter.to.toISOString());
    return apiFetch<ManagedBooking[]>(`/api/v1/bookings/all?${params}`);
  },

  /**
   * Löscht eine Buchung endgültig — nur für die Administration.
   *
   * Der Regelfall ist `cancel`: Es gibt den Zeitraum ebenso frei, lässt aber
   * nachvollziehbar, dass es die Buchung gab. Löschen scheitert mit
   * BOOKING_HAS_RECORDS, sobald eine Fahrt oder ein Fahrtenbucheintrag daranhängt.
   */
  remove: (id: string) => apiFetch<void>(`/api/v1/bookings/${id}`, { method: 'DELETE' }),

  /**
   * Entscheidet über eine Buchung.
   *
   * Bei einer Ablehnung ist die Begründung Pflicht — das prüft dasselbe Schema
   * auch im Server.
   */
  decide: (id: string, decision: ApprovalDecision, comment?: string) =>
    apiFetch<{ id: string; status: BookingStatus }>(`/api/v1/bookings/${id}/decide`, {
      method: 'POST',
      body: JSON.stringify({ decision, comment }),
    }),
};
